import { useContext } from "react";
import { NavLink } from "react-router-dom";
import styles from "./Sidebar.module.scss";
import classNames from "classnames";
import { useIsAuthenticated } from "@azure/msal-react";
import { AuthContext } from "../../context/AuthContext";
import { IconEnum } from "../../utils/IconEnum";
import { LogoIcon } from "../Icons/Logo";
import { Button } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAlert } from "../../context/AlertContext";
import { useMsal } from "@azure/msal-react";

interface NavItem {
  path: string;
  label: string;
  icon?: string;
  type: "button" | "link";
  onClick?: () => void;
}

export const Sidebar = () => {
  const { instance } = useMsal();
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const { currentUser, loading, needsRegistration } = authContext;
  const isAuthenticated = useIsAuthenticated();
  const { setAlert } = useAlert();

  const isVolunteer = currentUser?.userType === "volunteer";

  const recommendedEventId = (
    currentUser?.userRecommendedEvent as { current_event_id?: string } | null
  )?.current_event_id;

  const handleNoRecommendedEvent = () => {
    setAlert(
      true,
      "info",
      "Aktuálně pro vás nemáme žádnou doporučenou akci. Mrkněte na přehled akcí.",
    );
  };

  const volunteerNav: NavItem[] = [
    {
      path: recommendedEventId ? `/event/${recommendedEventId}` : "",
      label: "Chci pomáhat",
      type: "button",
      onClick: recommendedEventId ? undefined : handleNoRecommendedEvent,
    },
    {
      path: "/volunteer/events",
      label: "Přehled akcí",
      icon: "FormatListBulletedIcon",
      type: "link",
    },
    {
      path: "/volunteer/favorite",
      label: "Moje akce",
      icon: "FavoriteBorderIcon",
      type: "link",
    },
  ];

  const orgNav: NavItem[] = [
    {
      path: "/organisation/new-event",
      label: "Vytvořit akci",
      icon: "AddIcon",
      type: "button",
    },
    {
      path: "/organisation/events",
      label: "Přehled akcí",
      icon: "LayersOutlinedIcon",
      type: "link",
    },
    {
      path: "/organisation/documents",
      label: "Dokumenty",
      icon: "AssignmentTurnedInOutlinedIcon",
      type: "link",
    },
    {
      path: "/organisation/evidence",
      label: "Evidence",
      icon: "PeopleOutlineIcon",
      type: "link",
    },
  ];

  const handleLogout = async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: "/login",
      });
    } catch (error) {
      console.error("Logout failed:", error);
      setAlert(
        true,
        "error",
        "Odhlášení se nezdařilo. Zkuste to prosím znovu.",
      );
    }
  };

  // Not authenticated – show public sidebar
  if (!isAuthenticated) {
    return (
      <div className={styles.sidebar}>
        <div className={styles.logo}>
          <LogoIcon />
        </div>
        <nav className={styles.navPublic}>
          <ul className={styles.listPublic}>
            <li className={styles.item}>
              pro více možností se musíte přihlásit
            </li>
            <li className={styles.item}>
              <NavLink to="/login" className={styles.link}>
                <Button variant="contained" fullWidth>
                  Přihlásit se
                </Button>
              </NavLink>
            </li>
            <li className={styles.item}>
              <NavLink to="/register" className={styles.link}>
                <Button variant="outlined" fullWidth>
                  Registrovat se
                </Button>
              </NavLink>
            </li>
          </ul>
        </nav>
      </div>
    );
  }

  // Authenticated but userType not yet known – show only logo to avoid wrong nav flash
  if (loading || (!currentUser && !needsRegistration)) {
    return (
      <div className={styles.sidebar}>
        <div className={styles.logo}>
          <LogoIcon />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.sidebar, {
        [styles.orgSidebar]: !isVolunteer,
      })}
    >
      <NavLink
        to={isVolunteer ? "/volunteer/events" : "/organisation/events"}
        className={styles.logo}
      >
        <LogoIcon />
      </NavLink>
      <nav className={styles.nav}>
        <ul className={styles.list}>
          {isVolunteer
            ? volunteerNav.map((item, idx) => {
                const IconComponent = item.icon ? IconEnum[item.icon] : null;
                return (
                  <li key={idx} className={styles.item}>
                    {item.onClick ? (
                      <Button
                        variant={item.type === "button" ? "contained" : "text"}
                        fullWidth
                        onClick={item.onClick}
                      >
                        {IconComponent && <IconComponent />}
                        {item.label}
                      </Button>
                    ) : (
                      <NavLink
                        to={item.path}
                        className={({ isActive }) =>
                          classNames(styles.link, {
                            [styles.active]: isActive,
                          })
                        }
                      >
                        {item.type === "button" ? (
                          <Button variant="contained" fullWidth>
                            {IconComponent && <IconComponent />}
                            {item.label}
                          </Button>
                        ) : (
                          <Button variant="text" fullWidth>
                            {IconComponent && <IconComponent />}
                            {item.label}
                          </Button>
                        )}
                      </NavLink>
                    )}
                  </li>
                );
              })
            : orgNav.map((item, idx) => {
                const IconComponent = item.icon ? IconEnum[item.icon] : null;
                return (
                  <li key={idx} className={styles.item}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        classNames(styles.link, {
                          [styles.active]: isActive,
                        })
                      }
                    >
                      {item.type === "button" ? (
                        <Button variant="contained" fullWidth>
                          {IconComponent && <IconComponent />}
                          {item.label}
                        </Button>
                      ) : (
                        <Button variant="text" fullWidth>
                          {IconComponent && <IconComponent />}
                          {item.label}
                        </Button>
                      )}
                    </NavLink>
                  </li>
                );
              })}
          <li className={styles.item}>
            <Button variant="text" fullWidth onClick={handleLogout}>
              <LogoutIcon />
              Odhlásit se
            </Button>
          </li>
        </ul>
      </nav>
    </div>
  );
};
